<template>
  <div class="serverside-data-table">
    <report-generate
      v-if="report"
      :dialog="dialogReport"
      :items="report.items.filter(item => item.show === undefined || item.show)"
      :provider="report.provider"
      :filename="report.filename"
      :filters="appliedFilters"
      @close="dialogReport = false"
    />

    <v-form v-if="filters !== undefined" @submit.prevent="search">
      <v-row>
        <v-col
          v-for="filter in filters"
          :key="filter.field"
          cols="12"
          md="3"
        >
          <data-table-input-filter
            v-if="
              filter.type === 'text' ||
                filter.type === 'number' ||
                filter.type === 'email'
            "
            v-model="filter.model"
            :filter="filter"
            dense="compact"
          />

          <data-table-select-filter
            v-if="filter.type == 'select'"
            v-model="filter.model"
            :filter="filter"
            :multiple="filter.multiple"
            :clearable="filter.clearable"
            dense="compact"
          />

          <data-table-multi-select-filter
            v-if="filter.type == 'multi-select'"
            v-model="filter.model"
            :filter="filter"
            dense="compact"
          />

          <data-table-date-interval-filter
            v-if="filter.type == 'date-interval'"
            v-model="filter.model"
            :filter="filter"
            dense="compact"
          />

          <date-picker
            v-if="filter.type == 'date-picker'"
            v-model="filter.model"
            :label="filter.label"
            dense="compact"
          />

          <data-table-auto-complete-filter
            v-if="filter.type == 'autocomplete'"
            v-model="filter.model"
            :filter="filter"
            :multiple="filter.multiple"
            dense="compact"
          />
        </v-col>
        <v-col v-if="filters.length > 0" cols="12" md="3">
          <v-btn
            color="primary"
            :disabled="loading"
            size="large"
            block
            type="submit"
            class="no-print"
          >
            Filtrar
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <!-- The component can either receive a pre-built metadata table as scope or build one receiving the data as prop -->
    <slot name="metadata" />

    <v-row v-if="metadata !== undefined" class="my-7" justify="end">
      <data-table-meta-data
        :headers="metadata.headers"
        :items="metadata.items"
        :loading="loading"
      />
    </v-row>

    <v-data-table-server
      v-model:items-per-page="_pagination.itemsPerPage"
      class="mt-10 text-body-2 rounded"
      :headers="filteredHeaders"
      :items-length="_pagination.total"
      :items="items"
      :loading="loading"
      :expand-on-click="expandOnClick"
      hide-no-data
      hover
      color="primary"
      @update:page="fetch(_pagination.page)"
    >
      <template #[`item.actions`]="data">
        <data-table-actions
          v-if="actions !== undefined"
          :item="data"
          :actions="actions"
          :resource="resource ?? ''"
        >
          <template
            v-for="slot in slotsNames.filter((s) => s === 'custom.actions')"
            #[slot]="actionData"
          >
            <slot :name="slot" v-bind="actionData" />
          </template>
        </data-table-actions>
      </template>

      <template
        v-for="slot in slotsNames.filter((s) => s !== 'custom.actions')"
        #[slot]="data"
      >
        <slot :name="slot" v-bind="data" />
      </template>

      <template v-if="vertical" #headers />

      <template v-if="vertical" #item="data">
        <tr>
          <td colspan="42" class="pa-4">
            <div v-for="header in filteredHeaders" :key="header.key" class="item-vertical d-flex py-1">
              <div class="d-flex align-center text-subtitle-2">
                <strong>{{ header.title }}:</strong>
              </div>
              <div class="flex-1-0 text-end">
                <template v-if="header.key !== 'actions'">
                  <template v-if="slotsNames.find(s => s.includes(header.key))">
                    <slot :name="`item.${header.key}`" v-bind="data" />
                  </template>
                  <template v-else>
                    {{ data.item[header.key] }}
                  </template>
                </template>

                <data-table-actions
                  v-else-if="actions !== undefined"
                  :item="data"
                  :actions="actions"
                  :resource="resource ?? ''"
                >
                  <template
                    v-for="slot in slotsNames.filter((s) => s === 'custom.actions')"
                    #[slot]="actionData"
                  >
                    <slot :name="slot" v-bind="actionData" />
                  </template>
                </data-table-actions>
              </div>
            </div>
          </td>
        </tr>
      </template>

      <template #bottom>
        <v-row>
          <v-col v-if="!hideItemsPerPage" />
          <v-col :class="hideItemsPerPage ? 'd-flex justify-center' : ''">
            <v-pagination
              v-model="_pagination.page"
              class="py-4"
              total-visible="10"
              :length="_pagination.totalPages"
              density="compact"
              prev-icon="fa-solid fa-angle-left"
              next-icon="fa-solid fa-angle-right"
              @update:model-value="fetch($event)"
            />
          </v-col>
          <v-col v-if="!hideItemsPerPage" class="d-flex align-center">
            <v-row align="center" class="me-1">
              <v-spacer />
              <v-col cols="3">
                <span>Itens por Página</span>
              </v-col>
              <v-col cols="3" class="d-flex align-center">
                <v-select
                  v-model="_pagination.itemsPerPage"
                  variant="outlined"
                  density="compact"
                  hide-details
                  :items="[10, 25, 50, 100]"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </v-data-table-server>
  </div>
</template>

<script lang="ts" setup generic="T">
import { computed, onMounted, ref, Ref, useSlots } from 'vue'

import DataTableInputFilter from '@/components/datatable/DataTableInputFilter.vue'
import DataTableSelectFilter from '@/components/datatable/DataTableSelectFilter.vue'
import DataTableMultiSelectFilter from '@/components/datatable/DataTableMultiSelectFilter.vue'
import DataTableDateIntervalFilter from '@/components/datatable/DataTableDateIntervalFilter.vue'
import DataTableAutoCompleteFilter from '@/components/datatable/DataTableAutoCompleteFilter.vue'
import DataTableActions from '@/components/datatable/DataTableActions.vue'
import DatePicker from '@/components/form/DatePicker.vue'
import DataTableMetaData from '@/components/datatable/DataTableMetaData.vue'
import ReportGenerate from '@/components/ReportGenerate.vue'

import type {
  DataTableHeader,
  DataTableProvider,
  DefaultDataTableActions,
  DataTablePagination,
  DataTableMeta,
  DataTableFilter,
} from '../../types/DataTable'

import type PaginatedResponse from '../../types/PaginatedResponse'
import type { Report } from '@/types/Report'
import { useLoader } from '@/stores/loader'

// Composables

const loader = useLoader()

// Props

interface DataTableProps {
  provider: DataTableProvider<T>
  headers: DataTableHeader[]
  filters: DataTableFilter[]
  metadata?: DataTableMeta
  resource?: string
  report?: Report
  actions?: DefaultDataTableActions
  pagination?: DataTablePagination
  expandOnClick?: boolean
  hideItemsPerPage?: boolean
}

const {
  provider,
  headers,
  filters,
  metadata,
  resource,
  report,
  actions,
  pagination,
  expandOnClick,
  vertical,
  hideItemsPerPage,
} = defineProps<DataTableProps & { vertical?: boolean }>()

// Slots

const slots = useSlots()

// Refs

const items: Ref<T[]> = ref([])

const _pagination: Ref<DataTablePagination> = ref({
  page: pagination?.page ?? 1,
  totalPages: pagination?.total ?? 1,
  itemsPerPage: pagination?.itemsPerPage ?? 25,
  total: pagination?.total ?? 1,
})

const dialogReport: Ref<boolean> = ref(false)

// Computeds

const slotsNames = computed(() => Object.keys(slots))

const loading = computed(() => loader.loading)

// This shit is not a ComputedRef because Vue reactivity unwraps to it's current value
// So actually f.value is a fucking string and i don't know how to tell Typescript this
// without using the cursed `as unknown as string` cast
const appliedFilters = computed(
  () => filters.filter(f => f.model !== null && (f.value as unknown as string) !== '').map(
    f => `${f.field}=${f.value as unknown as string}`,
  ).join('&'),
)

const filteredHeaders = computed(
  () => headers.filter(
    f => f.show === undefined || f.show(),
  ),
)

// Functions

function search() {
  _pagination.value.page = 1
  fetch(_pagination.value.page)
}

async function fetch(page: number): Promise<void> {
  loader.increment()
  try {
    await Promise.all(filters.map(f => f.ready))

    const response = await provider(
      page,
      appliedFilters.value,
      _pagination.value.itemsPerPage,
    )

    updatePagination(response.data)

    items.value = response.data.data
  } finally {
    loader.decrement()
  }
}

function updatePagination(data: PaginatedResponse<unknown>): void {
  const root = data.meta ?? data

  _pagination.value.page = root.current_page
  _pagination.value.itemsPerPage = root.per_page
  _pagination.value.total = root.total ?? 0
  _pagination.value.totalPages = root.last_page
}

function openDialogReport(): void {
  dialogReport.value = true
}

onMounted(
  () => fetch(_pagination.value.page),
)

defineExpose({
  openDialogReport,
  fetch,
  search,
})
</script>

<script lang="ts">
// Instance type for callers that type a template ref to this component
// (e.g. `useTemplateRef<DataTableType>('datatable')`), mirroring the
// `TServersideDatatableType` export from TServersideDatatable.vue. Declared
// manually (rather than via `InstanceType<typeof DataTable>`) because this
// component is generic (`<script setup generic="T">`), which InstanceType
// can't resolve; the exposed methods below don't depend on T anyway.
export interface DataTableType {
  openDialogReport: () => void
  fetch: (page: number) => Promise<void>
  search: () => void
}
</script>

<style scoped>
.item-vertical:not(:last-child) {
  border-bottom: thin dashed #0000001f !important;
}
</style>
